import React, { useState, useMemo } from 'react';
import { ReviewCard } from '../components/evidence/ReviewCard';

const STATUSES = ['pending', 'approved', 'rejected'];

/**
 * Evidence Log and Review Queue in one place.
 *
 * They were separate pages showing the same rows: the log to read them, the queue
 * to act on them. A trainer working through a student's evidence now filters by
 * apprentice and status here, and reviews without changing page.
 */
export function EvidenceReviewPage({
  profile, evidence, allProfiles, myApprentices, getUnitName,
  onApprove, onReject, onUpdateNote, onEditEvidence, onLightbox, onPDF,
}) {
  const [apprFilter, setApprFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');

  const readOnly = profile.role === 'supervisor';
  const nameOf = (id) => allProfiles.find(p => p.id === id)?.name || 'Unknown apprentice';

  // Only people this user is responsible for; entries are already filtered upstream
  const visible = useMemo(() => {
    const mine = new Set((myApprentices || []).map(a => a.id));
    return (evidence || []).filter(e => e.status !== 'draft' && (mine.size === 0 || mine.has(e.apprentice_id)));
  }, [evidence, myApprentices]);

  const counts = useMemo(() => {
    const base = { all: visible.length, pending: 0, approved: 0, rejected: 0 };
    for (const e of visible) if (base[e.status] !== undefined) base[e.status] += 1;
    return base;
  }, [visible]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = visible.filter(e => {
      if (apprFilter !== 'all' && e.apprentice_id !== apprFilter) return false;
      if (statusFilter !== 'all' && e.status !== statusFilter) return false;
      if (!q) return true;
      return [e.job_number, e.vehicle_rego, e.unit_code, e.unit_codes, e.fault_reason]
        .some(v => (v || '').toLowerCase().includes(q));
    });
    // Oldest first while reviewing — the queue should drain in order. Otherwise
    // newest first, which is how you'd read a log.
    return list.sort((a, b) => (statusFilter === 'pending'
      ? (a.date || '').localeCompare(b.date || '')
      : (b.date || '').localeCompare(a.date || '')));
  }, [visible, apprFilter, statusFilter, search]);

  // Per-apprentice pending counts for the filter chips
  const pendingBy = useMemo(() => {
    const m = {};
    for (const e of visible) if (e.status === 'pending') m[e.apprentice_id] = (m[e.apprentice_id] || 0) + 1;
    return m;
  }, [visible]);

  return (
    <div>
      <div className="page-hdr">
        <div>
          <div className="page-title">Evidence</div>
          <div className="page-sub">
            {readOnly ? 'View-only — approval is done by the trainer · ' : ''}
            {counts.pending} awaiting review · {counts.approved} approved · {counts.rejected} returned
          </div>
        </div>
      </div>

      {/* Apprentice filter */}
      {(myApprentices || []).length > 0 && (
        <div className="atabs" style={{ marginBottom: 10 }}>
          <button className={`atab${apprFilter === 'all' ? ' on' : ''}`} onClick={() => setApprFilter('all')}>
            All apprentices
          </button>
          {myApprentices.map(a => (
            <button key={a.id} className={`atab${apprFilter === a.id ? ' on' : ''}`} onClick={() => setApprFilter(a.id)}>
              {a.name}
              {pendingBy[a.id] ? (
                <span style={{
                  marginLeft: 6, background: 'var(--accent)', color: '#0d1321', borderRadius: 100,
                  padding: '0 6px', fontSize: 10, fontWeight: 700,
                }}>{pendingBy[a.id]}</span>
              ) : null}
            </button>
          ))}
        </div>
      )}

      {/* Status filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {['all', ...STATUSES].map(s => (
          <button key={s} className={`atab${statusFilter === s ? ' on' : ''}`} onClick={() => setStatusFilter(s)}>
            {s === 'all' ? 'All' : s === 'rejected' ? 'Returned' : s.charAt(0).toUpperCase() + s.slice(1)}
            {' '}({counts[s]})
          </button>
        ))}
        <input
          className="finp"
          placeholder="Search job number, rego, unit…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ fontSize: 12, maxWidth: 260, marginLeft: 'auto' }}
        />
      </div>

      {shown.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">{statusFilter === 'pending' ? '✅' : '📋'}</div>
          <p>
            {statusFilter === 'pending'
              ? 'Nothing waiting for review.'
              : `No ${statusFilter === 'all' ? '' : statusFilter === 'rejected' ? 'returned ' : `${statusFilter} `}entries${apprFilter !== 'all' ? ' for this apprentice' : ''}.`}
          </p>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>
            Showing {shown.length} entr{shown.length === 1 ? 'y' : 'ies'}
            {statusFilter === 'pending' && shown.length > 1 ? ' — oldest first' : ''}
          </div>
          {shown.map(e => (
            <ReviewCard
              key={e.id}
              entry={e}
              apprenticeName={apprFilter === 'all' ? nameOf(e.apprentice_id) : null}
              getUnitName={getUnitName}
              readOnly={readOnly}
              collapsible
              onApprove={onApprove}
              onReject={onReject}
              onUpdateNote={onUpdateNote}
              onEdit={onEditEvidence}
              onLightbox={onLightbox}
              onPDF={onPDF}
            />
          ))}
        </>
      )}
    </div>
  );
}
